import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
  ArrowForward as ArrowForwardIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import SalesReportViewer from '../components/SaleReportViewer';
import DailySaleSummaryReportViewer from '../components/DailySaleSummaryReportViewer';

const ReportsPage = () => {
  const [selectedReport, setSelectedReport] = useState(null);

  const reports = [
    {
      id: 'sales',
      title: 'Sales Report',
      description: 'Comprehensive sales analysis with revenue breakdown, VAT summary, and payment methods',
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      color: '#4caf50',
      bgColor: '#e8f5e9',
      available: true,
      features: ['Revenue analysis', 'VAT breakdown', 'Payment methods', 'Time period filters'],
    },
    {
      id: 'daily-summary',
      title: 'Daily Sale Summary',
      description: 'Daily breakdown of sales with items sold and revenue by date',
      icon: <CalendarIcon sx={{ fontSize: 40 }} />,
      color: '#00bcd4',
      bgColor: '#e0f7fa',
      available: true,
      features: ['Daily breakdown', 'Items sold tracking', 'Revenue by date', 'Monthly views'],
    },
    {
      id: 'inventory',
      title: 'Inventory Report',
      description: 'Stock levels, low stock alerts, and inventory valuation',
      icon: <InventoryIcon sx={{ fontSize: 40 }} />,
      color: '#ff9800',
      bgColor: '#fff3e0',
      available: false,
      features: ['Stock levels', 'Low stock alerts', 'Valuation', 'Movement history'],
    },
    {
      id: 'customer',
      title: 'Customer Report',
      description: 'Customer purchase history, top customers, and loyalty analysis',
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: '#2196f3',
      bgColor: '#e3f2fd',
      available: false,
      features: ['Purchase history', 'Top customers', 'Loyalty metrics', 'Demographics'],
    },
    {
      id: 'product',
      title: 'Product Performance',
      description: 'Best selling products, profit margins, and category analysis',
      icon: <ShoppingCartIcon sx={{ fontSize: 40 }} />,
      color: '#9c27b0',
      bgColor: '#f3e5f5',
      available: false,
      features: ['Best sellers', 'Profit margins', 'Category analysis', 'Trends'],
    },
    {
      id: 'financial',
      title: 'Financial Report',
      description: 'Profit & loss statements, balance sheets, and cash flow',
      icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
      color: '#f44336',
      bgColor: '#ffebee',
      available: false,
      features: ['P&L statement', 'Balance sheet', 'Cash flow', 'Expenses'],
    },
    {
      id: 'tax',
      title: 'Tax Report',
      description: 'VAT returns, tax liability, and compliance reports',
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      color: '#00bcd4',
      bgColor: '#e0f7fa',
      available: false,
      features: ['VAT returns', 'Tax liability', 'Compliance', 'Audit trail'],
    },
  ];

  const handleOpenReport = (reportId) => {
    setSelectedReport(reportId);
  };

  const handleCloseReport = () => {
    setSelectedReport(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pt: 3 }}>
      <Container maxWidth="lg">
        {/* Page Header - Inside Container */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box
              sx={{
                p: 1.5,
                bgcolor: '#667eea',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AssessmentIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} color="text.primary">
                Reports & Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Generate comprehensive business reports and insights
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Reports Grid */}
        <Grid container spacing={3} sx={{ pb: 4 }}>
          {reports.map((report) => (
            <Grid item xs={12} md={6} key={report.id}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s',
                  cursor: report.available ? 'pointer' : 'not-allowed',
                  opacity: report.available ? 1 : 0.6,
                  '&:hover': report.available
                    ? {
                        transform: 'translateY(-8px)',
                        boxShadow: 6,
                      }
                    : {},
                }}
                onClick={() => report.available && handleOpenReport(report.id)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: report.bgColor,
                        color: report.color,
                        mr: 2,
                      }}
                    >
                      {report.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6" fontWeight={700}>
                          {report.title}
                        </Typography>
                        {!report.available && (
                          <Chip
                            label="Coming Soon"
                            size="small"
                            sx={{
                              bgcolor: '#ff9800',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {report.description}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="text.secondary"
                      sx={{ mb: 1, display: 'block' }}
                    >
                      KEY FEATURES:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {report.features.map((feature, index) => (
                        <Chip
                          key={index}
                          label={feature}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {report.available && (
                    <Button
                      fullWidth
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        background: `linear-gradient(135deg, ${report.color} 0%, ${report.color}dd 100%)`,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${report.color}dd 0%, ${report.color}bb 100%)`,
                        },
                      }}
                    >
                      Generate Report
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Report Dialogs */}
      <SalesReportViewer
        open={selectedReport === 'sales'}
        onClose={handleCloseReport}
      />
      <DailySaleSummaryReportViewer
        open={selectedReport === 'daily-summary'}
        onClose={handleCloseReport}
      />
    </Box>
  );
};

export default ReportsPage;